import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  ChevronLeftIcon,
  ArrowRightOnRectangleIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import SkyToggle from '@/components/ui/sky-toggle';
import { cn } from '@/utils/helpers';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { toggleSidebarCollapsed, toggleTheme } from '@/store/slices/uiSlice';
import { logout } from '@/store/slices/authSlice';

// Custom macOS-style colorful icons
const PlannerIcon = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="calBg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#F9FAFB"/>
        <stop offset="100%" stopColor="#F3F4F6"/>
      </linearGradient>
      <linearGradient id="calRed" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#EF4444"/>
        <stop offset="100%" stopColor="#DC2626"/>
      </linearGradient>
    </defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#calBg)" stroke="#D1D5DB" strokeWidth="1.5"/>
    <path d="M10.5 32 Q10.5 10.5 32 10.5 L88 10.5 Q109.5 10.5 109.5 32 L109.5 38 L10.5 38 Z" fill="url(#calRed)"/>
    <circle cx="35" cy="24" r="4" fill="#FCA5A5"/>
    <circle cx="85" cy="24" r="4" fill="#FCA5A5"/>
    <text x="60" y="82" textAnchor="middle" fontSize="42" fontWeight="bold" fill="#1F2937">17</text>
  </svg>
);

const TracksIcon = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="tracksBg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#A78BFA"/>
        <stop offset="100%" stopColor="#7C3AED"/>
      </linearGradient>
    </defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#tracksBg)"/>
    {/* Progress path */}
    <path d="M32 90 L32 70 L55 70 L55 50 L78 50 L78 30 L92 30" stroke="white" strokeOpacity="0.35" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    {/* Completed node 1 */}
    <circle cx="32" cy="90" r="10" fill="#10B981"/>
    <path d="M27 90 L30 93 L37 86" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Completed node 2 */}
    <circle cx="55" cy="70" r="10" fill="#10B981"/>
    <path d="M50 70 L53 73 L60 66" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Completed node 3 */}
    <circle cx="78" cy="50" r="10" fill="#10B981"/>
    <path d="M73 50 L76 53 L83 46" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Current node - pulsing effect */}
    <circle cx="92" cy="30" r="12" fill="white"/>
    <circle cx="92" cy="30" r="6" fill="#7C3AED"/>
  </svg>
);

const HabitsIcon = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="habitHeader" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#FDBA74"/>
        <stop offset="100%" stopColor="#FB923C"/>
      </linearGradient>
      <linearGradient id="habitPaper" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#F9FAFB"/>
        <stop offset="100%" stopColor="#F3F4F6"/>
      </linearGradient>
    </defs>
    {/* Card body */}
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#habitPaper)" stroke="#D1D5DB" strokeWidth="1.5"/>
    {/* Orange header */}
    <path d="M10.5 32 Q10.5 10.5 32 10.5 L88 10.5 Q109.5 10.5 109.5 32 L109.5 36 L10.5 36 Z" fill="url(#habitHeader)"/>

    {/* Row 1 - checked */}
    <rect x="26" y="44" width="14" height="14" rx="3" fill="#F97316"/>
    <path d="M29 51 L33 55 L40 48" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <rect x="48" y="47" width="40" height="8" rx="4" fill="#FDBA74"/>

    {/* Row 2 - checked */}
    <rect x="26" y="64" width="14" height="14" rx="3" fill="#F97316"/>
    <path d="M29 71 L33 75 L40 68" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <rect x="48" y="67" width="32" height="8" rx="4" fill="#FDBA74"/>

    {/* Row 3 - unchecked */}
    <rect x="26" y="84" width="14" height="14" rx="3" fill="white" stroke="#FDBA74" strokeWidth="1.5"/>
    <rect x="48" y="87" width="36" height="8" rx="4" fill="#FED7AA"/>
  </svg>
);

const NotesIcon = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="noteYellow" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#FCD34D"/>
        <stop offset="100%" stopColor="#FBBF24"/>
      </linearGradient>
      <linearGradient id="notePaper" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#F9FAFB"/>
        <stop offset="100%" stopColor="#F3F4F6"/>
      </linearGradient>
    </defs>
    {/* Notepad body - off-white/cream */}
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#notePaper)" stroke="#D1D5DB" strokeWidth="1.5"/>
    {/* Yellow header */}
    <path d="M10.5 32 L10.5 32 Q10.5 10.5 32 10.5 L88 10.5 Q109.5 10.5 109.5 32 L109.5 35 L10.5 35 Z" fill="url(#noteYellow)"/>
    {/* Notebook lines */}
    <line x1="25" y1="50" x2="95" y2="50" stroke="#D1D5DB" strokeWidth="2"/>
    <line x1="25" y1="65" x2="95" y2="65" stroke="#D1D5DB" strokeWidth="2"/>
    <line x1="25" y1="80" x2="95" y2="80" stroke="#D1D5DB" strokeWidth="2"/>
    <line x1="25" y1="95" x2="95" y2="95" stroke="#D1D5DB" strokeWidth="2"/>
  </svg>
);

const BlogIcon = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="blogBg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#F97316"/>
        <stop offset="100%" stopColor="#EA580C"/>
      </linearGradient>
    </defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#blogBg)"/>
    {/* Stylized pen/pencil */}
    <g transform="translate(60,60) rotate(45)">
      <rect x="-8" y="-35" width="16" height="55" rx="2" fill="white"/>
      <path d="M-8,20 L0,38 L8,20" fill="white"/>
      <rect x="-8" y="-35" width="16" height="10" rx="2" fill="#FDBA74"/>
    </g>
  </svg>
);

const SettingsIcon = ({ size = 40 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="settingsBg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#94A3B8"/>
        <stop offset="100%" stopColor="#64748B"/>
      </linearGradient>
    </defs>
    <rect x="10" y="10" width="100" height="100" rx="22" fill="url(#settingsBg)"/>
    {/* Sliders design */}
    {/* Slider 1 */}
    <rect x="28" y="35" width="64" height="6" rx="3" fill="white" fillOpacity="0.4"/>
    <circle cx="72" cy="38" r="10" fill="white"/>
    {/* Slider 2 */}
    <rect x="28" y="57" width="64" height="6" rx="3" fill="white" fillOpacity="0.4"/>
    <circle cx="48" cy="60" r="10" fill="white"/>
    {/* Slider 3 */}
    <rect x="28" y="79" width="64" height="6" rx="3" fill="white" fillOpacity="0.4"/>
    <circle cx="62" cy="82" r="10" fill="white"/>
  </svg>
);

const navigation = [
  { name: 'Planner', href: '/planner', tourId: 'nav-planner', IconComponent: PlannerIcon },
  { name: 'Tracks', href: '/tracks', tourId: 'nav-tracks', IconComponent: TracksIcon },
  { name: 'Habits', href: '/habits', tourId: 'nav-habits', IconComponent: HabitsIcon },
  { name: 'Notes', href: '/notes', tourId: 'nav-notes', IconComponent: NotesIcon },
  { name: 'Blog', href: '/blog', tourId: 'nav-blog', IconComponent: BlogIcon },
];

interface SidebarProps {
  bgConfig?: {
    gradient: string;
    pattern: string;
    accentColor: string;
    sidebarAccent: string;
    patternColor: string;
    patternColorDark: string;
  };
}

export function Sidebar({ bgConfig: _bgConfig }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { sidebarCollapsed, theme } = useAppSelector((state) => state.ui);
  const isDarkMode = theme === 'dark';
  const { user } = useAppSelector((state) => state.auth);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  // Close More menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  // Dock effect state - include Settings (+1)
  const totalNavItems = navigation.length + 1;
  const [mouseY, setMouseY] = useState<number | null>(null);
  const [scales, setScales] = useState<number[]>(Array(totalNavItems).fill(1));
  const navRef = useRef<HTMLUListElement>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const animationRef = useRef<number>();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  // macOS dock magnification effect
  const baseSize = sidebarCollapsed ? 36 : 40;
  const maxScale = sidebarCollapsed ? 1.4 : 1.15;
  const effectRange = sidebarCollapsed ? 90 : 70;

  const calculateScales = useCallback((mousePosition: number | null) => {
    if (mousePosition === null) {
      return Array(totalNavItems).fill(1);
    }

    return Array(totalNavItems).fill(0).map((_, index) => {
      const itemCenter = index * (baseSize + 8) + baseSize / 2;
      const distance = Math.abs(mousePosition - itemCenter);

      if (distance > effectRange) return 1;

      // Cosine-based magnification like macOS
      const normalizedDistance = distance / effectRange;
      const scale = 1 + (maxScale - 1) * Math.cos(normalizedDistance * Math.PI / 2);

      return scale;
    });
  }, [baseSize, maxScale, effectRange, totalNavItems]);

  // Smooth animation loop
  useEffect(() => {
    const animate = () => {
      const targetScales = calculateScales(mouseY);

      setScales(prevScales => {
        // Ensure we always have the right number of scales
        const newScales = Array(totalNavItems).fill(1);
        for (let i = 0; i < totalNavItems; i++) {
          const current = prevScales[i] || 1;
          const target = targetScales[i] || 1;
          const diff = target - current;
          newScales[i] = current + diff * 0.3; // Faster lerp
        }
        return newScales;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [mouseY, calculateScales]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!navRef.current) return;
    const rect = navRef.current.getBoundingClientRect();
    setMouseY(e.clientY - rect.top);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMouseY(null);
  }, []);

  // Click bounce animation
  const iconRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleItemClick = (index: number) => {
    const icon = iconRefs.current[index];
    if (icon) {
      icon.style.transition = 'transform 0.1s cubic-bezier(0.4, 0, 0.2, 1)';
      icon.style.transform = 'scale(0.95)';
      setTimeout(() => {
        icon.style.transition = 'transform 0.15s cubic-bezier(0.4, 0, 0.2, 1)';
        icon.style.transform = '';
      }, 100);
    }
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen transition-all duration-300 ease-in-out overflow-hidden',
        'bg-white dark:bg-black border-r border-gray-200 dark:border-gray-800',
        sidebarCollapsed ? 'w-[72px]' : 'w-[244px]'
      )}
    >
      <div className="flex h-full flex-col px-4 py-5">
        {/* Logo */}
        <div className={cn(
          'mb-8 px-2',
          sidebarCollapsed && 'flex justify-center px-0'
        )}>
          {!sidebarCollapsed ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* 3D Ascending blocks logo */}
                <svg width="32" height="32" viewBox="30 50 300 330" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="top2" x1="0%" y1="100%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#FFEEE4"/>
                      <stop offset="100%" stopColor="#FFE4D6"/>
                    </linearGradient>
                    <linearGradient id="left2" x1="100%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#FDBA74"/>
                      <stop offset="100%" stopColor="#FB923C"/>
                    </linearGradient>
                    <linearGradient id="right2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#F97316"/>
                      <stop offset="100%" stopColor="#EA580C"/>
                    </linearGradient>
                  </defs>
                  <g>
                    <path d="M40 250 L40 340 L100 370 L100 280 Z" fill="url(#left2)"/>
                    <path d="M100 280 L100 370 L160 340 L160 250 Z" fill="url(#right2)"/>
                    <path d="M40 250 L100 220 L160 250 L100 280 Z" fill="url(#top2)"/>
                  </g>
                  <g>
                    <path d="M120 180 L120 340 L180 370 L180 210 Z" fill="url(#left2)"/>
                    <path d="M180 210 L180 370 L240 340 L240 180 Z" fill="url(#right2)"/>
                    <path d="M120 180 L180 150 L240 180 L180 210 Z" fill="url(#top2)"/>
                  </g>
                  <g>
                    <path d="M200 90 L200 340 L260 370 L260 120 Z" fill="url(#left2)"/>
                    <path d="M260 120 L260 370 L320 340 L320 90 Z" fill="url(#right2)"/>
                    <path d="M200 90 L260 60 L320 90 L260 120 Z" fill="url(#top2)"/>
                  </g>
                </svg>
                <span className="text-xl font-semibold text-gray-900 dark:text-white">
                  DayDrive
                </span>
              </div>
              <button
                onClick={() => dispatch(toggleSidebarCollapsed())}
                data-tour="sidebar-toggle"
                className="group relative w-8 h-8 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md hover:scale-105 active:scale-95 transition-all duration-200 flex items-center justify-center"
              >
                <ChevronLeftIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-orange-500 transition-colors" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => dispatch(toggleSidebarCollapsed())}
              data-tour="sidebar-toggle"
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {/* Collapsed - 3D blocks logo */}
              <svg width="28" height="28" viewBox="30 50 300 330" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="top2c" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#FFEEE4"/>
                    <stop offset="100%" stopColor="#FFE4D6"/>
                  </linearGradient>
                  <linearGradient id="left2c" x1="100%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#FDBA74"/>
                    <stop offset="100%" stopColor="#FB923C"/>
                  </linearGradient>
                  <linearGradient id="right2c" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#F97316"/>
                    <stop offset="100%" stopColor="#EA580C"/>
                  </linearGradient>
                </defs>
                <g>
                  <path d="M40 250 L40 340 L100 370 L100 280 Z" fill="url(#left2c)"/>
                  <path d="M100 280 L100 370 L160 340 L160 250 Z" fill="url(#right2c)"/>
                  <path d="M40 250 L100 220 L160 250 L100 280 Z" fill="url(#top2c)"/>
                </g>
                <g>
                  <path d="M120 180 L120 340 L180 370 L180 210 Z" fill="url(#left2c)"/>
                  <path d="M180 210 L180 370 L240 340 L240 180 Z" fill="url(#right2c)"/>
                  <path d="M120 180 L180 150 L240 180 L180 210 Z" fill="url(#top2c)"/>
                </g>
                <g>
                  <path d="M200 90 L200 340 L260 370 L260 120 Z" fill="url(#left2c)"/>
                  <path d="M260 120 L260 370 L320 340 L320 90 Z" fill="url(#right2c)"/>
                  <path d="M200 90 L260 60 L320 90 L260 120 Z" fill="url(#top2c)"/>
                </g>
              </svg>
            </button>
          )}
        </div>

        {/* Main Navigation */}
        <nav className="flex-1">
          <ul
            ref={navRef}
            className={cn("space-y-2", sidebarCollapsed && "space-y-2")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {navigation.map((item, index) => {
              const isActive =
                item.href === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.href);

              const scale = scales[index];
              const iconSize = sidebarCollapsed ? 36 : 32;

              return (
                <li
                  key={item.name}
                  ref={(el) => { itemRefs.current[index] = el; }}
                  style={sidebarCollapsed ? {
                    transform: `scale(${scale})`,
                    transformOrigin: 'left center',
                    transition: 'none',
                    zIndex: Math.round(scale * 10)
                  } : undefined}
                >
                  <NavLink
                    to={item.href}
                    data-tour={item.tourId}
                    onClick={() => handleItemClick(index)}
                    className={cn(
                      'group flex px-1 py-1 rounded-xl transition-all duration-200',
                      sidebarCollapsed
                        ? 'flex-col items-center justify-center gap-0.5'
                        : 'flex-row items-center gap-2.5',
                      !sidebarCollapsed && (isActive
                        ? 'bg-gray-100/80 dark:bg-gray-800/80'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-900/50')
                    )}
                    title={sidebarCollapsed ? item.name : undefined}
                  >
                    {/* Icon and text wrapper for dock effect */}
                    <div
                      className="flex items-center gap-2.5"
                      style={!sidebarCollapsed ? {
                        transform: `scale(${scale})`,
                        transformOrigin: 'left center',
                        transition: 'none'
                      } : undefined}
                    >
                      <div
                        ref={(el) => { iconRefs.current[index] = el; }}
                        className="shrink-0"
                      >
                        <item.IconComponent size={iconSize} />
                      </div>

                      {!sidebarCollapsed && (
                        <span className={cn(
                          'text-[15px]',
                          isActive
                            ? 'text-gray-900 dark:text-white font-medium'
                            : 'text-gray-600 dark:text-gray-300'
                        )}>
                          {item.name}
                        </span>
                      )}
                    </div>
                    {/* Active dot indicator when collapsed */}
                    {sidebarCollapsed && isActive && (
                      <div className="w-[3px] h-[3px] rounded-full bg-orange-400" />
                    )}
                  </NavLink>
                </li>
              );
            })}

            {/* Settings */}
            <li
              ref={(el) => { itemRefs.current[navigation.length] = el; }}
              style={sidebarCollapsed ? {
                transform: `scale(${scales[navigation.length] || 1})`,
                transformOrigin: 'left center',
                transition: 'none',
                zIndex: Math.round((scales[navigation.length] || 1) * 10)
              } : undefined}
            >
              <NavLink
                to="/settings"
                data-tour="nav-settings"
                onClick={() => handleItemClick(navigation.length)}
                className={cn(
                  'group flex px-1 py-1 rounded-xl transition-all duration-200',
                  sidebarCollapsed
                    ? 'flex-col items-center justify-center gap-0.5'
                    : 'flex-row items-center gap-2.5',
                  !sidebarCollapsed && (location.pathname.startsWith('/settings')
                    ? 'bg-gray-100/80 dark:bg-gray-800/80'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-900/50')
                )}
                title={sidebarCollapsed ? 'Settings' : undefined}
              >
                {/* Icon and text wrapper for dock effect */}
                <div
                  className="flex items-center gap-2.5"
                  style={!sidebarCollapsed ? {
                    transform: `scale(${scales[navigation.length] || 1})`,
                    transformOrigin: 'left center',
                    transition: 'none'
                  } : undefined}
                >
                  <div
                    ref={(el) => { iconRefs.current[navigation.length] = el; }}
                    className="shrink-0"
                  >
                    <SettingsIcon size={sidebarCollapsed ? 36 : 32} />
                  </div>

                  {!sidebarCollapsed && (
                    <span className={cn(
                      'text-[15px]',
                      location.pathname.startsWith('/settings')
                        ? 'text-gray-900 dark:text-white font-medium'
                        : 'text-gray-600 dark:text-gray-300'
                    )}>
                      Settings
                    </span>
                  )}
                </div>
                {/* Active dot indicator when collapsed */}
                {sidebarCollapsed && location.pathname.startsWith('/settings') && (
                  <div className="w-[3px] h-[3px] rounded-full bg-orange-400" />
                )}
              </NavLink>
            </li>
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-800 space-y-1">
          {/* Theme Toggle */}
          <div className={cn(
            'flex items-center py-2 px-3',
            sidebarCollapsed && 'justify-center px-0'
          )}>
            <SkyToggle
              checked={isDarkMode}
              onChange={() => dispatch(toggleTheme())}
              size={sidebarCollapsed ? 8 : 10}
            />
          </div>

          {/* More Menu */}
          <div ref={moreMenuRef} className="relative">
            <button
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className={cn(
                'w-full group flex items-center gap-2.5 px-2 py-1.5 rounded-xl transition-all duration-200',
                'hover:bg-gray-50 dark:hover:bg-gray-900/50',
                sidebarCollapsed && 'justify-center px-1'
              )}
            >
              <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                <EllipsisHorizontalIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              {!sidebarCollapsed && (
                <span className="text-[15px] text-gray-600 dark:text-gray-300">More</span>
              )}
            </button>

            {/* More Dropdown */}
            {showMoreMenu && (
              <div
                className={cn(
                  'fixed py-2 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50',
                  'bg-white dark:bg-gray-900',
                  sidebarCollapsed ? 'w-48 ml-16' : 'w-52'
                )}
                style={{
                  bottom: '80px',
                  left: sidebarCollapsed ? '8px' : '16px'
                }}
              >
                <button
                  onClick={() => {
                    handleLogout();
                    setShowMoreMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Log out</span>
                </button>
              </div>
            )}
          </div>

          {/* User Profile */}
          <NavLink
            to="/settings"
            className={cn(
              'group flex items-center gap-2.5 px-2 py-1.5 rounded-xl transition-all duration-200',
              'hover:bg-gray-50 dark:hover:bg-gray-900/50',
              sidebarCollapsed && 'justify-center px-1'
            )}
          >
            <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-pink-500">
              <span className="text-sm font-medium text-white">
                {user?.name?.substring(0, 1).toUpperCase() || 'U'}
              </span>
            </div>
            {!sidebarCollapsed && (
              <span className="text-[15px] text-gray-600 dark:text-gray-300 truncate">
                {user?.name || 'Profile'}
              </span>
            )}
          </NavLink>
        </div>
      </div>
    </aside>
  );
}
